import React, { Component } from "react";
import {
    Button,
    createStyles,
    Theme,
    Typography,
    Paper,
    Box,
    CircularProgress,
    Grid,
    Chip,
    Collapse,
    TablePagination,
} from "@material-ui/core";
import { withStyles, WithStyles } from "@material-ui/core/styles";
import {
    ExpandMore,
    Assessment,
    ExpandLess,
    Refresh,
    Clear,
    Sync,
    GetApp,
} from "@material-ui/icons";
import { Divider, IconButton, Tooltip } from "@material-ui/core";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import DateUtils from "@date-io/date-fns";
import SearchUser from "../Search/SearchUser";
import gameApi from "../../api/games";
import Moment from "moment";
import { showSnackbar } from "../SharedComponents/Notifier";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import schalaLogo from "../../assets/images/qr-logo.png";

const styles = (theme: Theme) =>
    createStyles({
        container: {
            backgroundColor: "#fff",
            width: "100%",
            flex: 1,
            minHeight: 0,
            boxSizing: "border-box",
            padding: theme.spacing(0, 2, 2),
            display: "flex",
            flexDirection: "column",
            [theme.breakpoints.down("xs")]: {
                padding: theme.spacing(0, 1.5, 1.5),
            },
        },
        header: {
            marginBottom: theme.spacing(3),
        },
        title: {
            fontSize: "1.5rem",
            fontWeight: 600,
            color: "#1a1a1a",
            marginBottom: theme.spacing(0.5),
        },
        subtitle: {
            fontSize: "0.875rem",
            color: "#666",
        },
        filterSection: {
            padding: theme.spacing(2.5),
            border: "1px solid #e0e0e0",
            borderRadius: 0,
            backgroundColor: "#fafafa",
            width: "100%",
        },
        filterRow: {
            marginBottom: theme.spacing(2),
            "&:last-child": {
                marginBottom: 0,
            },
        },
        label: {
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "#666",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: theme.spacing(1),
        },
        chipGroup: {
            display: "flex",
            gap: theme.spacing(1),
            flexWrap: "wrap",
        },
        rangeChip: {
            fontSize: "0.8125rem",
            fontWeight: 500,
            height: 28,
        },
        dateRow: {
            display: "flex",
            gap: theme.spacing(2),
            alignItems: "flex-end",
        },
        generateBtn: {
            height: 48,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            boxShadow: "none",
            marginTop: theme.spacing(2),
            "&:hover": {
                boxShadow: "none",
            },
        },
        updateBtn: {
            backgroundColor: "#f50057",
            color: "#fff",
            "&:hover": {
                backgroundColor: "#d81b60",
            },
        },
        statsContainer: {
            display: "flex",
            gap: theme.spacing(2),
            padding: theme.spacing(1.75, 0),
            backgroundColor: "#fafafa",
            width: "100%",
        },
        statBox: {
            flex: 1,
            padding: theme.spacing(2),
            border: "1px solid #e0e0e0",
            borderRadius: 6,
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgb(0 0 0 / 5%)",
        },
        statLabel: {
            fontSize: "0.75rem",
            color: "#666",
            marginBottom: theme.spacing(0.5),
            fontWeight: 500,
        },
        statValue: {
            fontSize: "2rem",
            fontWeight: 600,
            color: "#1a1a1a",
            lineHeight: 1,
        },
        resultsList: {
            border: "1px solid #e0e0e0",
            borderRadius: 0,
            overflow: "hidden",
            width: "100%",
            flex: 1,
            minHeight: 0,
        },
        userRow: {
            padding: theme.spacing(2),
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            transition: "background-color 0.15s ease",
            overflow: "hidden",
            "&:hover": {
                backgroundColor: "#fafafa",
            },
            "&:last-child": {
                borderBottom: "none",
            },
        },
        userHeader: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
        },
        userInfo: {
            flex: 1,
        },
        userName: {
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "#1a1a1a",
            marginBottom: theme.spacing(0.25),
        },
        userMeta: {
            fontSize: "0.8125rem",
            color: "#666",
            wordBreak: "break-word",
        },
        gamesCount: {
            fontSize: "1.5rem",
            fontWeight: 600,
            color: theme.palette.primary.main,
            marginRight: theme.spacing(2),
        },
        gamesLabel: {
            fontSize: "0.75rem",
            fontWeight: 600,
            color: theme.palette.primary.main,
            lineHeight: 1,
        },
        expandBtn: {
            marginTop: theme.spacing(1.5),
            fontSize: "0.8125rem",
            textTransform: "none",
            fontWeight: 500,
            color: "#666",
        },
        gamesList: {
            marginTop: theme.spacing(1.5),
            paddingTop: theme.spacing(1.5),
            borderTop: "1px solid #f0f0f0",
        },
        gameRow: {
            display: "flex",
            alignItems: "center",
            padding: "2px 0",
            marginBottom: theme.spacing(0.5),
            borderRadius: 4,
            "&:hover": {
                backgroundColor: "#f5f5f5",
            },
        },
        gameDate: {
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: "#1a1a1a",
            flex: "0 0 100px",
            [theme.breakpoints.down("xs")]: {
                flex: "0 0 75px",
                fontSize: "0.75rem",
            },
        },
        gameCourse: {
            fontSize: "0.8125rem",
            color: "#555",
            flex: 1,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
        },
        gameCode: {
            fontSize: "0.75rem",
            fontWeight: 600,
            fontFamily: "monospace",
            color: theme.palette.primary.main,
            padding: "2px 8px",
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: 4,
            flex: "0 0 auto",
        },
        emptyState: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: theme.spacing(6, 2),
            color: "#999",
            minHeight: 200,
        },
        pagination: {
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
        },
    });

interface Props extends WithStyles<typeof styles> { }

interface State {
    startDate: Date | null;
    endDate: Date | null;
    selectedUser: any;
    loading: boolean;
    results: any[];
    expandedCards: { [key: number]: boolean };
    page: number;
    rowsPerPage: number;
    totalCount: number;
    totalGames: number;
    selectedRange: string;
    hasChanges: boolean;
}

class GameCreatorReportDialog extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            startDate: Moment("2000-01-01").toDate(),
            endDate: new Date(),
            selectedUser: null,
            loading: false,
            results: [],
            expandedCards: {},
            page: 0,
            rowsPerPage: 10,
            totalCount: 0,
            totalGames: 0,
            selectedRange: "all",
            hasChanges: false,
        };
    }

    handleStartDateChange = (date: Date | null) => this.setState({ startDate: date, selectedRange: "", hasChanges: true });
    handleEndDateChange = (date: Date | null) => this.setState({ endDate: date, selectedRange: "", hasChanges: true });

    handleUserSelected = (user: any) => {
        this.setState({ selectedUser: user, page: 0, hasChanges: true });
    };

    resetFilters = () => {
        this.setState({
            startDate: Moment("2000-01-01").toDate(),
            endDate: new Date(),
            selectedRange: "all",
            selectedUser: null,
            expandedCards: {},
            page: 0,
            hasChanges: false,
        });
    };

    handleChangePage = (event: unknown, newPage: number) => {
        this.setState({ page: newPage }, this.generateReport);
    };

    handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ rowsPerPage: parseInt(event.target.value, 10), page: 0 }, this.generateReport);
    };

    toggleCardExpansion = (index: number) => {
        this.setState((prev) => ({
            expandedCards: {
                ...prev.expandedCards,
                [index]: !prev.expandedCards[index],
            },
        }));
    };

    setQuickRange = (range: string) => {
        let start: Date | null = Moment().toDate();
        let end: Date | null = new Date();

        switch (range) {
            case "week":
                start = Moment().subtract(1, "week").toDate();
                break;
            case "month":
                start = Moment().subtract(1, "month").toDate();
                break;
            case "year":
                start = Moment().subtract(1, "year").toDate();
                break;
            case "all":
                start = Moment("2000-01-01").toDate();
                end = Moment().toDate();
                break;
            default:
                break;
        }

        this.setState({ startDate: start, endDate: end, selectedRange: range, page: 0, hasChanges: true });
    };

    exportToPDF = async () => {
        const { startDate, endDate, selectedUser, totalCount } = this.state;
        if (totalCount === 0) {
            showSnackbar("No results to export");
            return;
        }

        this.setState({ loading: true });
        try {
            const startTimestamp = startDate ? Moment(startDate).startOf("day").valueOf() : undefined;
            const endTimestamp = endDate ? Moment(endDate).endOf("day").valueOf() : undefined;
            let userId: string | undefined = undefined;
            if (selectedUser) {
                if (selectedUser.object && selectedUser.object._id) userId = selectedUser.object._id;
                else if (selectedUser._id) userId = selectedUser._id;
                else if (selectedUser.id) userId = selectedUser.id;
            }

            // Fetch up to 500 results for the PDF
            const response = await gameApi.getCreatorsReport(startTimestamp, endTimestamp, userId, 0, 500);

            if (response.status === 200) {
                const allResults = response.data.data || [];
                const doc = new jsPDF("l", "mm", "a4"); // Landscape for more space

                // Add Logo and Branding (Using pre-loaded image from DOM for reliability)
                const logoImg = document.getElementById("schala-report-logo") as HTMLImageElement;
                if (logoImg) {
                    try {
                        // Positioning on the right side
                        doc.addImage(logoImg, "PNG", 260, 10, 22, 22);
                        doc.setFontSize(14);
                        doc.setTextColor(63, 81, 181); // Primary indigo
                        doc.setFont("helvetica", "bold");
                        doc.text("SCHALA", 260, 36);
                        doc.setFont("helvetica", "normal");
                    } catch (e) {
                        console.warn("PDF Logo warning:", e);
                    }
                }

                // Add title
                doc.setFontSize(22);
                doc.setTextColor(33, 33, 33);
                doc.text("Game Creator Report", 14, 22);

                // Add metadata
                doc.setFontSize(10);
                doc.setTextColor(100, 100, 100);
                doc.text(`Generated on: ${Moment().format("DD/MM/YYYY HH:mm")}`, 14, 30);

                const dateRangeStr = `Period: ${Moment(startDate).format("DD/MM/YYYY")} - ${Moment(endDate).format("DD/MM/YYYY")}`;
                doc.text(dateRangeStr, 14, 35);

                if (selectedUser) {
                    doc.text(`User Filter: ${selectedUser.name}`, 14, 40);
                }

                // Stats summary
                doc.setFontSize(11);
                doc.setTextColor(0, 0, 0);
                doc.setFont("helvetica", "bold");
                doc.text(`Total Creators: ${response.data.total || 0}`, 14, 50);
                doc.text(`Total Games: ${response.data.totalGames || 0}`, 60, 50);
                doc.setFont("helvetica", "normal");

                // Prepare table data
                const tableData: any[][] = [];
                allResults.forEach((row: any) => {
                    const creatorName = `${row.user.name} ${row.user.lastName || ""}`.trim();
                    const email = row.user.email || "-";
                    const club = row.user.homeClub || "-";

                    if (row.games && row.games.length > 0) {
                        row.games.forEach((game: any) => {
                            tableData.push([
                                creatorName,
                                email,
                                club,
                                Moment(game.date).format("DD/MM/YYYY"),
                                game.course || "Unknown",
                                game.code || "-"
                            ]);
                        });
                    } else {
                        tableData.push([
                            creatorName,
                            email,
                            club,
                            "-",
                            "-",
                            "-"
                        ]);
                    }
                });

                autoTable(doc, {
                    startY: 55,
                    head: [["Creator", "Email", "Home Club", "Game Date", "Course", "Game Code"]],
                    body: tableData,
                    theme: "grid",
                    headStyles: {
                        fillColor: [63, 81, 181], // Primary color
                        textColor: [255, 255, 255],
                        fontSize: 10,
                        fontStyle: "bold",
                        halign: "center"
                    },
                    bodyStyles: {
                        fontSize: 9,
                        textColor: [50, 50, 50],
                    },
                    columnStyles: {
                        0: { cellWidth: 45 }, // Creator
                        1: { cellWidth: 60 }, // Email
                        2: { cellWidth: 50 }, // Club
                        3: { cellWidth: 25, halign: "center" }, // Date
                        4: { cellWidth: "auto" }, // Course
                        5: { cellWidth: 25, halign: "center" }, // Code
                    },
                    margin: { top: 20 },
                    didDrawPage: (data) => {
                        // Footer with page number
                        const str = "Page " + (doc as any).internal.getNumberOfPages();
                        doc.setFontSize(8);
                        const pageSize = doc.internal.pageSize;
                        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
                        doc.text(str, data.settings.margin.left, pageHeight - 10);
                    }
                });

                doc.save(`Game_Creator_Report_${Moment().format("YYYY-MM-DD")}.pdf`);
            } else {
                showSnackbar(response.error || "Failed to generate report for export");
            }
        } catch (error) {
            console.error("PDF Export Error:", error);
            showSnackbar("An error occurred while exporting PDF");
        } finally {
            this.setState({ loading: false });
        }
    };

    generateReport = async () => {
        const { startDate, endDate, selectedUser, page, rowsPerPage } = this.state;
        this.setState({ loading: true, hasChanges: false });
        try {
            const startTimestamp = startDate ? Moment(startDate).startOf("day").valueOf() : undefined;
            const endTimestamp = endDate ? Moment(endDate).endOf("day").valueOf() : undefined;
            let userId: string | undefined = undefined;
            if (selectedUser) {
                // SearchUser wraps the real user in .object, and the ID is likely _id
                if (selectedUser.object && selectedUser.object._id) {
                    userId = selectedUser.object._id;
                } else if (selectedUser._id) {
                    userId = selectedUser._id;
                } else if (selectedUser.id) {
                    userId = selectedUser.id;
                }
            }

            const response = await gameApi.getCreatorsReport(startTimestamp, endTimestamp, userId, page, rowsPerPage);
            if (response.status === 200) {
                const responseData = response.data;
                const results = responseData.data || [];
                const total = responseData.total || 0;
                const totalGames = responseData.totalGames || 0;

                this.setState({ results: results, totalCount: total, totalGames: totalGames, loading: false });
            } else {
                showSnackbar(response.error || "Failed to generate report");
                this.setState({ loading: false });
            }
        } catch (error) {
            showSnackbar("An error occurred while generating the report");
            this.setState({ loading: false });
        }
    };

    render() {
        const { classes } = this.props;
        const { startDate, endDate, loading, results, selectedUser, expandedCards, page, rowsPerPage, totalCount, totalGames, selectedRange, hasChanges } = this.state;
        const loadResults = totalCount > 0;

        return (
            <Box className={classes.container}>
                {/* Logo for PDF - Hidden from UI */}
                <div style={{ display: "none" }}>
                    <img id="schala-report-logo" src={schalaLogo} alt="Schala Logo" />
                </div>

                <Paper className={classes.filterSection} elevation={0}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Box className={classes.filterRow}>
                                <Typography className={classes.label}>User Filter</Typography>
                                <Box display="flex" alignItems="center" borderBottom={!selectedUser ? "1px solid #ccc" : ""}>
                                    {selectedUser ? (
                                        <Chip
                                            label={selectedUser.name}
                                            onDelete={() => this.handleUserSelected(null)}
                                            color="primary"
                                            variant="outlined"
                                            className={classes.rangeChip}
                                        />
                                    ) : (
                                        <SearchUser
                                            onUserSelected={this.handleUserSelected}
                                            title="Search by name or email..."
                                            autoClearText={false}
                                            style={{ width: "100%" }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box className={classes.filterRow}>
                                <Typography className={classes.label}>Time Period</Typography>
                                <Box className={classes.chipGroup}>
                                    <Chip
                                        label="Week"
                                        onClick={() => this.setQuickRange("week")}
                                        color={selectedRange === "week" ? "primary" : "default"}
                                        variant="outlined"
                                        size="small"
                                        className={classes.rangeChip}
                                    />
                                    <Chip
                                        label="Month"
                                        onClick={() => this.setQuickRange("month")}
                                        color={selectedRange === "month" ? "primary" : "default"}
                                        variant="outlined"
                                        size="small"
                                        className={classes.rangeChip}
                                    />
                                    <Chip
                                        label="Year"
                                        onClick={() => this.setQuickRange("year")}
                                        color={selectedRange === "year" ? "primary" : "default"}
                                        variant="outlined"
                                        size="small"
                                        className={classes.rangeChip}
                                    />
                                    <Chip
                                        label="All Time"
                                        onClick={() => this.setQuickRange("all")}
                                        color={selectedRange === "all" ? "primary" : "default"}
                                        variant="outlined"
                                        size="small"
                                        className={classes.rangeChip}
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box className={classes.filterRow} style={{ marginBottom: 0 }}>
                                <Typography className={classes.label}>Custom Date Range</Typography>
                                <Box className={classes.dateRow}>
                                    <MuiPickersUtilsProvider utils={DateUtils}>
                                        <KeyboardDatePicker
                                            autoOk
                                            variant="inline"
                                            inputVariant="outlined"
                                            size="small"
                                            format="dd/MM/yyyy"
                                            label="From"
                                            value={startDate}
                                            onChange={this.handleStartDateChange}
                                            views={["year", "month", "date"]}
                                            openTo="year"
                                            style={{ flex: 1 }}
                                        />
                                        <KeyboardDatePicker
                                            autoOk
                                            variant="inline"
                                            inputVariant="outlined"
                                            size="small"
                                            format="dd/MM/yyyy"
                                            label="To"
                                            value={endDate}
                                            onChange={this.handleEndDateChange}
                                            views={["year", "month", "date"]}
                                            openTo="year"
                                            style={{ flex: 1 }}
                                        />
                                    </MuiPickersUtilsProvider>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box display="flex" alignItems="center" mt={2}>
                        <Button
                            variant="contained"
                            color={hasChanges ? "primary" : "primary"}
                            onClick={this.generateReport}
                            disabled={loading}
                            className={`${classes.generateBtn} ${hasChanges ? classes.updateBtn : ""}`}
                            style={{ flex: 1, marginTop: 0, transition: "all 0.3s ease" }}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : hasChanges ? <Sync /> : <Refresh />}
                        >
                            {hasChanges ? "Update Report" : "Generate Report"}
                        </Button>

                        <div style={{
                            width: hasChanges ? 60 : 0,
                            marginLeft: hasChanges ? 8 : 0,
                            opacity: hasChanges ? 1 : 0,
                            overflow: "hidden",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            display: "flex",
                            justifyContent: "flex-end"
                        }}>
                            <Tooltip title="Clear Filters">
                                <Button
                                    variant="outlined"
                                    onClick={this.resetFilters}
                                    disabled={loading}
                                    style={{
                                        minWidth: 60,
                                        width: 60,
                                        height: 48,
                                        borderColor: "#e0e0e0",
                                        color: "#666",
                                        padding: 0
                                    }}
                                >
                                    <Clear />
                                </Button>
                            </Tooltip>
                        </div>
                    </Box>
                </Paper>

                <Box className={classes.statsContainer}>
                    <Paper className={classes.statBox} elevation={0}>
                        <Typography className={classes.statLabel}>
                            {selectedUser ? "Games Created" : "Total Creators"}
                        </Typography>
                        <Typography className={classes.statValue}>
                            {loadResults ? (selectedUser ? totalGames : totalCount) : "-"}
                        </Typography>
                    </Paper>
                    <Paper className={classes.statBox} elevation={0} style={{ visibility: !selectedUser ? "visible" : "hidden", opacity: !selectedUser ? 1 : 0 }}>
                        <Typography className={classes.statLabel}>Total Games</Typography>
                        <Typography className={classes.statValue}>{loadResults ? totalGames : "-"}</Typography>
                    </Paper>
                </Box>

                {results.length > 0 ? (
                    <Paper className={classes.resultsList} elevation={0}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            px={2}
                            py={1.5}
                            bgcolor="#f8f9fa"
                            borderBottom="1px solid #e0e0e0"
                        >
                            <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#444" }}>
                                Report Details
                            </Typography>
                            <Button
                                size="small"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={this.exportToPDF}
                                style={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "0.8125rem",
                                    padding: "4px 12px",
                                    borderRadius: 4,
                                    backgroundColor: "rgba(63, 81, 181, 0.04)"
                                }}
                            >
                                Export PDF
                            </Button>
                        </Box>
                        {results.map((row, index) => (
                            <Box className={classes.userRow} key={index}>
                                <Box className={classes.userHeader}>
                                    <Box className={classes.userInfo}>
                                        <Typography className={classes.userName}>
                                            {`${row.user.name} ${row.user.lastName}`}
                                        </Typography>
                                        <Typography className={classes.userMeta}>
                                            {row.user.email}
                                            {row.user.homeClub && ` • ${row.user.homeClub}`}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" flexDirection="column" alignItems="center" mr={2}>
                                        <Typography className={classes.gamesCount} style={{ marginRight: 0 }}>
                                            {row.totalGames}
                                        </Typography>
                                        <Typography className={classes.gamesLabel}>
                                            {row.totalGames === 1 ? "game" : "games"}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Button
                                    className={classes.expandBtn}
                                    onClick={() => this.toggleCardExpansion(index)}
                                    endIcon={expandedCards[index] ? <ExpandLess /> : <ExpandMore />}
                                    size="small"
                                    style={{
                                        padding: "0px",
                                    }}
                                >
                                    {expandedCards[index] ? "Hide" : "Show"} {"details"}
                                </Button>

                                <Collapse in={expandedCards[index]}>
                                    <Box className={classes.gamesList}>
                                        {row.games.map((game: any, gIndex: number) => (
                                            <Box className={classes.gameRow} key={gIndex}>
                                                <Typography className={classes.gameDate}>
                                                    {Moment(game.date).format("DD MMM YYYY")}
                                                </Typography>
                                                <Tooltip title={game.course || "Unknown Course"}>
                                                    <Typography className={classes.gameCourse}>
                                                        {game.course || "Unknown Course"}
                                                    </Typography>
                                                </Tooltip>
                                                <Typography className={classes.gameCode}>
                                                    {game.code}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Collapse>
                            </Box>
                        ))}
                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page}
                            onChangePage={this.handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onChangeRowsPerPage={this.handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            className={classes.pagination}
                        />
                    </Paper>
                ) : (
                    <Box className={classes.emptyState}>
                        {loading ? (
                            <CircularProgress size={40} color="primary" />
                        ) : (
                            <>
                                <Assessment style={{ fontSize: 64, color: "#ddd", marginBottom: 16 }} />
                                <Typography variant="body1" style={{ fontWeight: 500 }}>
                                    No results found
                                </Typography>
                                <Typography variant="body2">
                                    Adjust your filters and try again
                                </Typography>
                            </>
                        )}
                    </Box>
                )}
            </Box>
        );
    }
}

export default withStyles(styles)(GameCreatorReportDialog);
